import React, { Component } from 'react';

// eslint-disable-next-line lodash/import-scope
import { throttle } from 'lodash';
import PropTypes from 'prop-types';
import {
  VictoryChart,
  VictoryLine,
  VictoryLabel,
  VictoryAxis,
  VictoryScatter,
  VictoryCursorContainer,
  Line,
} from 'victory';

// Utils
import { colors, type } from '@nerdwallet/base-styles/variables';
import { Tracking } from '@nerdwallet/nw-client-lib';

import { THROTTLE_INTERVAL } from '../amortization-calculator/analytics-constants';

let clickedX;

class ResultsChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      drag: false,
      xValue: this.props.cursorDefaultX,
    };

    this.dispatchX.bind(this);
    this.onMouseUp.bind(this);
    this.toggleTooltip.bind(this);
    this.onCursorChange = throttle(this.onCursorChange, THROTTLE_INTERVAL * 3);
  }

  componentDidMount() {
    const { browser } = this.props;
    if (browser.isMobile || browser.isTablet) {
      this.toggleTooltip(this.props.cursorData[0], true);
    }
  }

  onCursorChange(value) {
    if (value !== null) {
      const xValue = Math.round(value);
      if (!xValue < 1) {
        if (this.state.drag) {
          this.setState({ xValue });
          if (this.props.action) {
            this.dispatchX(xValue);
          }
        }
        clickedX = xValue;
      }
    }
  }

  onMouseUp() {
    if (clickedX > 0) {
      this.setState({ drag: false, xValue: clickedX });
      if (this.props.action) {
        this.dispatchX(clickedX);
      }
      if (this.props.tracking) {
        Tracking.formInputChanged({
          fieldValue: clickedX,
          formName: this.props.tracking.formName,
          fieldName: this.props.tracking.fieldName,
        });
      }
    }
    this.setState({ drag: false });
  }

  toggleTooltip(vals, toggle) {
    if (this.props.action) {
      this.props.dispatch({
        type: this.props.action,
        tooltip: {
          showTooltip: toggle,
          data: {
            x: vals.x,
            y: vals.y,
          },
        },
      });
    }
  }

  dispatchX(val) {
    this.props.dispatch({
      type: this.props.action,
      xValue: val,
    });
  }

  static renderChartLabel(x, y, label) {
    return (
      <VictoryLabel
        x={x}
        y={y}
        text={label}
        style={{
          letterSpacing: 1.6,
          fill: colors['neutral-dark'],
          fontFamily: type['font-family-gotham'],
        }}
      />
    );
  }

  renderChartAxis(chartWidth, xAxis, tickValues) {
    const { browser } = this.props;

    const offset = 50;
    const style = {
      tickLabels: {
        fontSize: type['font-size-small'],
        fill: colors['neutral-dark'],
        fontFamily: type['font-family-gotham'],
        padding: 5,
        letterSpacing: 1.4,
      },
    };

    if (xAxis) {
      const tickCount = browser.isMobile
        ? tickValues[1] / 5
        : tickValues[1] / 2;
      return (
        <VictoryAxis
          crossAxis
          domain={tickValues}
          offsetY={offset}
          standalone={false}
          tickCount={tickCount}
          width={chartWidth}
          style={{
            axis: { stroke: colors['neutral-light'] },
            grid: { stroke: colors.transparent },
            ...style,
          }}
        />
      );
    }

    return (
      <VictoryAxis
        dependentAxis
        offsetX={offset}
        standalone={false}
        tickValues={tickValues}
        tickCount={2}
        width={chartWidth}
        style={{
          axis: { stroke: colors.transparent },
          grid: { stroke: colors['neutral-light'] },
          ...style,
        }}
      />
    );
  }

  render() {
    const { browser, data, chartWidth } = this.props;

    return (
      <VictoryChart
        width={chartWidth}
        containerComponent={
          <VictoryCursorContainer
            cursorDimension="x"
            onCursorChange={(value) => this.onCursorChange(value)}
            events={{
              onMouseDown: () => {
                this.setState({ drag: true });
              },
              onTouchStart: () => {
                this.setState({ drag: true });
                this.toggleTooltip(this.props.cursorData[0], false);
              },
              onTouchEnd: () => {
                this.onMouseUp();
                this.toggleTooltip(this.props.cursorData[0], true);
              },
              onClick: () => {
                this.onMouseUp();
                if (browser.isMobile || browser.isTablet) {
                  this.toggleTooltip(this.props.cursorData[0], true);
                }
              },
            }}
            cursorComponent={
              <Line
                style={{
                  pointerEvents: 'none',
                  stroke:
                    browser.isMobile || browser.isTablet
                      ? 'transparent'
                      : colors['neutral-lighter'],
                }}
              />
            }
          />
        }
      >
        <svg>
          {ResultsChart.renderChartLabel(5, 30, this.props.yLabel)}
          {ResultsChart.renderChartLabel(610, 240, this.props.xLabel)}
          {this.renderChartAxis(chartWidth, false, this.props.yTickValues)}
          {this.renderChartAxis(chartWidth, true, this.props.domain)}
        </svg>
        {data.map((d) => (
          <VictoryLine
            key={d.name}
            name={d.name}
            width={chartWidth}
            standalone={false}
            data={d.data}
            x={d.x}
            y={d.y}
            style={d.style}
          />
        ))}
        {this.state.xValue ? (
          <VictoryLine
            data={[
              {
                x: this.state.xValue,
                y: this.props.cursorYValues.min,
              },
              {
                x: this.state.xValue,
                y: this.props.cursorYValues.max,
              },
            ]}
            style={{ data: { stroke: this.props.cursorColor } }}
          />
        ) : null}
        {this.state.xValue ? (
          <VictoryScatter
            data={this.props.cursorData}
            style={{
              data: {
                fill: colors.white,
                stroke: this.props.cursorColor,
                strokeWidth: 3,
              },
            }}
            size={browser.isMobile ? 12 : 7}
            events={[
              {
                target: 'data',
                eventHandlers: {
                  onMouseOver: () => [
                    {
                      target: 'data',
                      mutation: (props) => {
                        this.toggleTooltip(
                          {
                            x: props.datum.x,
                            y: props.datum.y,
                          },
                          true
                        );
                      },
                    },
                  ],
                  onMouseOut: () => [
                    {
                      target: 'data',
                      mutation: (props) => {
                        this.toggleTooltip(
                          {
                            x: props.datum.x,
                            y: props.datum.y,
                          },
                          false
                        );
                      },
                    },
                  ],
                },
              },
            ]}
          />
        ) : null}
      </VictoryChart>
    );
  }
}

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
ResultsChart.propTypes = {
  data: PropTypes.array.isRequired,
  xLabel: PropTypes.string.isRequired,
  yLabel: PropTypes.string.isRequired,
  domain: PropTypes.array.isRequired,
  yTickValues: PropTypes.array.isRequired,
  chartWidth: PropTypes.number.isRequired,
  // cursor
  cursorData: PropTypes.array.isRequired,
  cursorDefaultX: PropTypes.number.isRequired,
  cursorYValues: PropTypes.object.isRequired,
  cursorColor: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
  action: PropTypes.string,
  tracking: PropTypes.object,
  browser: PropTypes.shape({
    isMobile: PropTypes.bool,
    isTablet: PropTypes.bool,
  }).isRequired,
};

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
ResultsChart.defaultProps = {
  action: null,
  tracking: null,
};

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
ResultsChart.displayName = 'ResultsChart';

export default ResultsChart;
